import { Flex, IconButtonProps, TooltipProps } from '@chakra-ui/react';
import { DateSelect, DateSelectOption } from '@gamma/ui/form-fields';
import moment from 'moment';
import { useCallback, useEffect, useRef } from 'react';
import { NavigateFunction, useLocation } from 'react-router';
import { dateRangeOptions, ROUTES } from '../../../../constants';
import {
  useDateRangeContext,
  useDateRangeQueryString,
} from '../../../../hooks';

interface DateSelectSyncProps {
  label?: string;
  isLabelHidden?: boolean;
  orientation?: 'vertical' | 'horizontal';
  dateSelectButtonConfig?: {
    toolTipProps: Omit<TooltipProps, 'children'>;
    iconButtonProps: IconButtonProps;
  };
  navigate: NavigateFunction;
  onDateRangeChange?: () => void;
}

export const getDateRangeOptionFromDates = (
  start: number,
  end: number,
  defaultOption?: DateSelectOption,
): DateSelectOption | undefined => {
  const diff = Math.abs(
    new Date(end * 1000).valueOf() - new Date(start * 1000).valueOf(),
  );
  for (const [range, ms] of Object.entries({
    months: 2630016000,
    weeks: 604800000,
    days: 86400000,
    hours: 3600000,
  })) {
    const qty = Math.round(diff / ms);
    if (qty > 0) {
      const option = dateRangeOptions.find(
        ([val, key]) => val === qty && range.includes(key),
      );
      if (option) return option;
    }
  }
  return defaultOption;
};

export const DateSelectSync = ({
  navigate,
  orientation,
  isLabelHidden = true,
  dateSelectButtonConfig,
  label = 'Select Date Range',
  onDateRangeChange,
}: DateSelectSyncProps) => {
  const location = useLocation();
  const { pathname } = location;

  const isOnSecurityOverview = pathname === ROUTES.home;

  const { start, end, cdate } = useDateRangeQueryString();
  const {
    dateRange,
    setDateRange,
    showCustomDateRangePicker,
    setShowCustomDateRangePicker,
  } = useDateRangeContext();

  const hasInitializedDashboardDates = useRef(false);

  const navigateWithParams = useCallback(
    (start: number, end: number, isCustomDate = false) => {
      // Create a fresh URLSearchParams from current location to avoid mutation issues
      const params = new URLSearchParams(window.location.search);
      params.set('start', String(start));
      params.set('end', String(end));
      params.set('cdate', String(isCustomDate));
      const searchString = params.toString();
      navigate(
        {
          pathname,
          search: searchString,
        },
        { replace: true }, // Use replace to update URL immediately without adding to history
      );
    },
    [navigate, pathname],
  );

  const navigateWithStartEnd = useCallback(() => {
    if (dateRange) {
      const end = moment().unix();
      const start = moment().subtract(dateRange[0], dateRange[1]).unix();
      const isCustomDate = !!showCustomDateRangePicker;
      navigateWithParams(start, end, isCustomDate);
    }
  }, [dateRange, showCustomDateRangePicker, navigateWithParams]);

  useEffect(() => {
    if (!isOnSecurityOverview) {
      // Reset flag when not on dashboard
      hasInitializedDashboardDates.current = false;
      return;
    }

    // Only set dates if they don't exist AND we haven't already initialized them
    if ((!start || !end) && !hasInitializedDashboardDates.current) {
      hasInitializedDashboardDates.current = true;

      const currentEnd = moment().unix();
      const currentStart = moment().subtract(7, 'days').unix();

      navigateWithParams(currentStart, currentEnd, false);
      setDateRange?.([7, 'days']);
      setShowCustomDateRangePicker?.(false);
    }
  }, [
    isOnSecurityOverview,
    start,
    end,
    navigateWithParams,
    setDateRange,
    setShowCustomDateRangePicker,
  ]);

  useEffect(() => {
    if (isOnSecurityOverview) return;

    const currentParams = new URLSearchParams(window.location.search);
    const urlHasDates = currentParams.get('start') && currentParams.get('end');

    if (!urlHasDates && dateRange) {
      navigateWithStartEnd();
    }
  }, [dateRange, navigateWithStartEnd, isOnSecurityOverview]);

  const handleOnChange = (
    startDate: Date,
    endDate: Date,
    option?: DateSelectOption | 'custom',
  ) => {
    if (setDateRange && option !== 'custom') {
      setDateRange(option);
      setShowCustomDateRangePicker?.(false);
    } else {
      setShowCustomDateRangePicker?.(true);
    }

    if (startDate && endDate) {
      const start = moment(startDate).unix();
      const end = moment(endDate).unix();
      navigateWithParams(start, end, option === 'custom');
    }

    onDateRangeChange?.();
  };

  const isCustomDate = (cdate && cdate === 'true') ?? showCustomDateRangePicker;

  let defaultValue: DateSelectOption | 'custom';

  if (isCustomDate) {
    defaultValue = 'custom';
  } else {
    defaultValue = getDateRangeOptionFromDates(start, end, dateRange) ??
      dateRange ?? [7, 'days'];
  }

  const customDates =
    isCustomDate && start && end
      ? [moment.unix(start).toDate(), moment.unix(end).toDate()]
      : undefined;

  return (
    <Flex
      zIndex="auto"
      position="relative"
      data-testid="date-select-container"
      justifyContent={orientation === 'vertical' ? 'start' : 'end'}
      cursor={'pointer'}
    >
      <DateSelect
        name="dateRangeSelect"
        label={label}
        isLabelHidden={isLabelHidden}
        allowCustom={true}
        customDates={customDates}
        onChange={handleOnChange}
        options={dateRangeOptions}
        defaultValue={defaultValue}
        orientation={orientation ?? 'horizontal'}
        pickerProps={{ maxDate: new Date() }}
        lapsedTimeDate={end * 1000}
        {...(dateSelectButtonConfig && {
          dateSelectButtonConfig: {
            iconButtonProps: {
              ...dateSelectButtonConfig?.iconButtonProps,
              isDisabled:
                dateSelectButtonConfig?.iconButtonProps?.isDisabled ||
                defaultValue === 'custom',
            },
            toolTipProps: {
              ...dateSelectButtonConfig?.toolTipProps,
            },
          },
        })}
      />
    </Flex>
  );
};
